import * as Yup from 'yup';
import {
  ANY_EXPERIENCE_OPTION,
  WaitlistPreferencesFields,
  WaitlistPreferencesForm,
  waitlistPreferencesSchema,
} from 'components/business/waitlist-dialog/WaitlistPreferencesForm';
import { Button, DialogActions, DialogContent, DialogForm, DialogHeading } from 'omakase';
import {
  ContactFields,
  WaitlistContactForm,
  contactSchema,
} from 'components/business/waitlist-dialog/WaitlistContactForm';
import { Formik, FormikHelpers } from 'formik';
import { LocalTime } from 'js-joda';
import { ReactNode, useState } from 'react';
import { WaitlistSuccessPage } from 'components/business/waitlist-dialog/WaitlistSuccessPage';
import { core } from '@tock/coretypes';
import { displayedTimesSelector } from 'selectors/displayedTimesSelectors';
import { getDetectedLocation, getIsAuthenticated } from 'selectors/appSelector';
import { twentyFourHourFormatter } from '@tock/i18n';
import { useSelector } from 'react-redux';

export type WaitlistRequestFields = WaitlistPreferencesFields & ContactFields;

type WaitlistRequestFormProps = {
  /**
   * Values to pre-populate on the request.
   */
  initialRequest: Partial<WaitlistRequestFields>;
  /**
   * Callback when form is dismissed.
   */
  onCancel(): void;
  /**
   *  Callback when request is submitted
   */
  onSubmit: (values: WaitlistRequestFields) => Promise<core.Waitlist | undefined>;
};

type RequestFormStep = {
  schema: Yup.AnyObjectSchema;
  component: ReactNode;
};

const preferencesStep: RequestFormStep = {
  schema: waitlistPreferencesSchema,
  component: <WaitlistPreferencesForm />,
};

const contactStep: RequestFormStep = {
  schema: contactSchema,
  component: <WaitlistContactForm />,
};

export const getInitialTimeRange = (availableTimes: string[], requestedStartTime?: string) => {
  const defaultTimeRange = {
    desiredStartTime: availableTimes[0],
    desiredEndTime: availableTimes[availableTimes.length - 1],
  };

  if (!requestedStartTime) {
    return defaultTimeRange;
  }

  try {
    const localTimeDesiredTime = LocalTime.parse(requestedStartTime);
    const desiredStartTime = twentyFourHourFormatter.format(localTimeDesiredTime.minusHours(1));
    const desiredEndTime = twentyFourHourFormatter.format(localTimeDesiredTime.plusHours(1));
    const isAvailableTime = (time: string) => availableTimes.includes(time);

    return {
      desiredStartTime: isAvailableTime(desiredStartTime)
        ? desiredStartTime
        : defaultTimeRange.desiredStartTime,
      desiredEndTime: isAvailableTime(desiredEndTime)
        ? desiredEndTime
        : defaultTimeRange.desiredEndTime,
    };
  } catch {
    return defaultTimeRange;
  }
};

export const WaitlistRequestForm = ({
  initialRequest,
  onCancel,
  onSubmit,
}: WaitlistRequestFormProps) => {
  const businessHours = useSelector(displayedTimesSelector);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const detectedLocation = useSelector(getDetectedLocation);

  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [submittedRequest, setSubmittedRequest] = useState<core.Waitlist>();

  const steps = isAuthenticated ? [preferencesStep] : [preferencesStep, contactStep];
  const isLastStep = currentStepIdx === steps.length - 1;
  const currentStep = steps[currentStepIdx];

  /**
   * Navigate through form steps and call onSubmit on last step
   * If successful we receive the waitlist response, errors are handled
   * by onSubmit and will resolve to undefined
   */
  const handleSubmit = async (
    values: WaitlistRequestFields,
    helpers: FormikHelpers<WaitlistRequestFields>
  ) => {
    if (isLastStep) {
      const result = await onSubmit(values);
      if (result) {
        setSubmittedRequest(result);
      }
    } else {
      // go to next step, reset touched to assure errors are displayed as fields are interacted with
      helpers.resetForm({ values });
      setCurrentStepIdx(currentStepIdx + 1);
    }
  };

  const handleBack = () => {
    setCurrentStepIdx(currentStepIdx - 1);
  };

  if (submittedRequest) {
    return (
      <WaitlistSuccessPage
        isAuthenticated={isAuthenticated}
        waitlistEntry={submittedRequest}
        onClose={onCancel}
      />
    );
  }

  return (
    <Formik<WaitlistRequestFields>
      initialValues={{
        desiredExperience: initialRequest.desiredExperience || ANY_EXPERIENCE_OPTION,
        desiredPartySize: initialRequest.desiredPartySize ?? '',
        desiredDates: initialRequest.desiredDates ?? [],
        ...getInitialTimeRange(businessHours, initialRequest.desiredStartTime),
        firstName: initialRequest.firstName ?? '',
        lastName: initialRequest.lastName ?? '',
        email: initialRequest.email ?? '',
        phone: initialRequest.phone ?? '',
        phoneCountryCode:
          initialRequest.phoneCountryCode || detectedLocation?.phoneCountryCode || '+1 US',
        isoCountryCode: initialRequest.isoCountryCode || detectedLocation?.isoCountryCode || 'US',
      }}
      validationSchema={currentStep.schema}
      onSubmit={handleSubmit}
    >
      <DialogForm>
        <DialogHeading onClose={onCancel}>Notify</DialogHeading>
        <DialogContent>{currentStep.component}</DialogContent>
        <DialogActions>
          {currentStepIdx > 0 && (
            <Button fullWidth onClick={handleBack} size="medium" variant="basic">
              Back
            </Button>
          )}
          <Button fullWidth size="medium" type="submit" variant="primary">
            {isLastStep ? 'Set Notify' : 'Continue'}
          </Button>
        </DialogActions>
      </DialogForm>
    </Formik>
  );
};
